import { CircleCheck, MailX } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	AcceptInviteForm,
	type AcceptInviteViolations,
} from "@/features/auth/components/accept-invite-form";
import { StatusCard } from "@/features/auth/components/status-card";
import { VerificationShell } from "@/features/auth/components/verification-shell";
import { useAcceptInvite } from "@/features/auth/hooks/use-accept-invite";
import { useInvitePreview } from "@/features/auth/hooks/use-invite-preview";
import type { AcceptInviteFormValues } from "@/features/auth/schemas/accept-invite-schema";
import { roleLabel } from "@/features/team/utils/format";
import { useAuth } from "@/store/use-auth";

const REDIRECT_DELAY_MS = 1800;

export function InvitePage() {
	const { token } = useParams();
	const navigate = useNavigate();
	const signIn = useAuth((state) => state.signIn);
	const session = useAuth((state) => state.session);

	const preview = useInvitePreview(token);
	const accept = useAcceptInvite(token);

	const [formError, setFormError] = useState<string>();
	const [violations, setViolations] = useState<AcceptInviteViolations>();

	useEffect(() => {
		if (!accept.isSuccess) return;
		const id = window.setTimeout(
			() => navigate("/", { replace: true }),
			REDIRECT_DELAY_MS,
		);
		return () => window.clearTimeout(id);
	}, [accept.isSuccess, navigate]);

	function onSubmit(values: AcceptInviteFormValues) {
		setFormError(undefined);
		setViolations(undefined);
		const email = preview.data?.email ?? "";
		accept.mutate(
			{ name: values.name, password: values.password },
			{
				onSuccess: (resp) => {
					signIn({
						token: resp.jwt,
						user: { name: values.name || email, email },
						provider: "password",
					});
				},
				onError: (err) => {
					if (err instanceof APIError) {
						const scoped: AcceptInviteViolations = {};
						if (err.violations.name) scoped.name = err.violations.name;
						if (err.violations.password)
							scoped.password = err.violations.password;
						if (scoped.name || scoped.password) {
							setViolations(scoped);
							return;
						}
						if (err.reason === "invalid-credentials") {
							setFormError("Senha incorreta. Use a senha da sua conta.");
							return;
						}
						if (err.reason === "already-member") {
							setFormError("Você já faz parte deste canteiro.");
							return;
						}
						if (err.reason === "already-account-member") {
							setFormError("Você já faz parte desta organização.");
							return;
						}
						setFormError(err.message || "Não foi possível aceitar o convite.");
						return;
					}
					setFormError("Não foi possível aceitar o convite. Tente novamente.");
				},
			},
		);
	}

	let content: ReactNode;

	if (accept.isSuccess) {
		content = (
			<StatusCard
				tone="success"
				icon={<CircleCheck />}
				title="Convite aceito!"
				description="Tudo certo. Estamos te levando para a sua obra."
			/>
		);
	} else if (!token || preview.isError) {
		const reason =
			preview.error instanceof APIError ? preview.error.reason : "";
		const accepted = reason === "invite-already-accepted";
		const expired = reason === "invite-expired";
		content = (
			<StatusCard
				tone="danger"
				icon={<MailX />}
				title={
					accepted
						? "Convite já aceito"
						: expired
							? "Convite expirado"
							: "Convite inválido"
				}
				description={
					accepted
						? "Este convite já foi utilizado. Faça login para acessar."
						: expired
							? "Este convite expirou. Peça à construtora para reenviar."
							: "Este link de convite é inválido ou não existe mais."
				}
				action={
					<Button
						type="button"
						className="status-card__action"
						onClick={() => navigate("/login")}
					>
						Ir para o login
					</Button>
				}
			/>
		);
	} else if (preview.isPending) {
		content = <InviteCardSkeleton />;
	} else {
		const invite = preview.data;
		const role = roleLabel(invite.role);
		const userExists = invite.user_exists;
		content = (
			<Card>
				<CardHeader className="invite-card__header">
					<h1 className="invite-card__title">Você foi convidado</h1>
					<p className="invite-card__description">
						Para entrar como <strong>{role}</strong> em{" "}
						<strong>{invite.account_name}</strong>
						{invite.worksite_name ? (
							<>
								{" "}
								— canteiro <strong>{invite.worksite_name}</strong>
							</>
						) : null}
						.{" "}
						{userExists
							? `Você já tem conta — confirme sua senha para entrar ${
									invite.worksite_name ? "neste canteiro" : "nesta organização"
								}.`
							: "Defina seu nome e uma senha para continuar."}
					</p>
				</CardHeader>
				<CardContent className="invite-card__body">
					{session && (
						<p className="invite-card__session-note">
							Você já está logado como{" "}
							<strong className="invite-card__session-email">
								{session.user.email}
							</strong>
							. Ao aceitar, entraremos com a conta do convite.
						</p>
					)}
					<AcceptInviteForm
						loading={accept.isPending}
						error={formError}
						violations={violations}
						userExists={userExists}
						onSubmit={onSubmit}
					/>
				</CardContent>
			</Card>
		);
	}

	return <VerificationShell>{content}</VerificationShell>;
}

function InviteCardSkeleton() {
	return (
		<Card>
			<CardHeader className="invite-card__header">
				<Skeleton className="invite-card__skeleton-title" />
				<div className="invite-card__skeleton-description">
					<Skeleton className="invite-card__skeleton-line" />
					<Skeleton className="invite-card__skeleton-line invite-card__skeleton-line--short" />
				</div>
			</CardHeader>
			<CardContent className="invite-card__body">
				<div className="form-group">
					<Skeleton className="invite-card__skeleton-label" />
					<Skeleton className="invite-card__skeleton-field" />
				</div>
				<div className="form-group">
					<Skeleton className="invite-card__skeleton-label" />
					<Skeleton className="invite-card__skeleton-field" />
				</div>
				<div className="form-group">
					<Skeleton className="invite-card__skeleton-label" />
					<Skeleton className="invite-card__skeleton-field" />
				</div>
				<Skeleton className="invite-card__skeleton-submit" />
			</CardContent>
		</Card>
	);
}
